import {
  Component,
  DestroyRef,
  HostListener,
  inject,
  OnInit,
} from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { MatDialogModule } from '@angular/material/dialog';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  GestureNavigationService,
  MobileDialogDragService,
  SwipeGuardService,
  UiDialogService,
} from '@ui';
import { ConnectivityActions } from './store/state/connectivity/connectivity.actions';
import { AppState } from './store/app.state';
import { TranslationService } from './services/translation.service';
import { InactivityModalComponent } from './components/modals/inactivity-modal/inactivity-modal.component';
import { AuthActions } from './store/state/auth/auth.actions';
import { MobileStatusBarService } from './services/mobile-status-bar.service';
import { MobileKeyboardService } from './services/mobile-keyboard.service';
import { MobileBackButtonService } from './services/mobile-back-button.service';
import { AuthService } from './services/auth.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, MatDialogModule],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly store$ = inject<Store<AppState>>(Store);
  private readonly translationService = inject(TranslationService);
  private readonly actions$ = inject(Actions);
  private readonly uiDialogService = inject(UiDialogService);
  private readonly mobileStatusBar = inject(MobileStatusBarService);
  private readonly mobileKeyboard = inject(MobileKeyboardService);
  private readonly authService = inject(AuthService);
  private readonly gestureNavigation = inject(GestureNavigationService);
  private readonly swipeGuard = inject(SwipeGuardService);
  private readonly mobileDialogDrag = inject(MobileDialogDragService);
  private readonly mobileBackButton = inject(MobileBackButtonService);

  constructor() {
    this.translationService.initLanguage();
    this.destroyRef.onDestroy(() => this.gestureNavigation.destroy());
  }

  @HostListener('window:keydown')
  @HostListener('window:mousemove')
  @HostListener('window:mousedown')
  public checkUserActivity(): void {
    this.store$.dispatch(ConnectivityActions.registerUserActivity());
  }

  @HostListener('document:visibilitychange')
  public refreshSessionOnResume(): void {
    if (document.visibilityState !== 'visible' || !navigator.onLine) {
      return;
    }

    void this.authService.ensureValidSession({
      allowBackgroundRefresh: true,
      isSilentRefresh: true,
    });
  }

  public ngOnInit(): void {
    void this.mobileStatusBar.init();
    this.mobileKeyboard.init();
    this.gestureNavigation.initEdgeNavigation();
    this.swipeGuard.init();
    this.mobileBackButton.init();

    this.enforceClickjackingProtection();
    this.store$.dispatch(ConnectivityActions.registerUserActivity());
    this.actions$
      .pipe(
        ofType(ConnectivityActions.showInactivityModal),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ secondsLeft }) => this.openInactivityModal(secondsLeft));
  }

  private enforceClickjackingProtection(): void {
    if (window.self === window.top) {
      return;
    }
    console.error('[SECURITY] App loaded in an iframe');

    if (!this.isFramedCrossOrigin()) {
      try {
        window.top!.location.href = window.self.location.href;
        return;
      } catch (e) {
        console.error('[SECURITY] Same-origin iframe breakout failed', e);
      }
    }

    this.store$.dispatch(
      AuthActions.logout({
        msg: 'For your security, this application cannot run inside an embedded frame. Open it in a full browser window.',
      }),
    );
  }

  private isFramedCrossOrigin(): boolean {
    try {
      if (window.self === window.top) {
        return false;
      }
      return window.top!.location.origin !== window.self.location.origin;
    } catch {
      return true;
    }
  }

  private openInactivityModal(secondsLeft: number): void {
    const countdown = secondsLeft > 0 ? secondsLeft : 30;
    this.uiDialogService.open(InactivityModalComponent, {
      id: 'inactivity-dialog',
      width: '475px',
      panelClass: 'modal-no-padding',
      disableClose: true,
      data: {
        secondsLeft: countdown,
      },
      bottomSheet: true,
    });
  }
}
